import { baseApi } from '../../services/baseApi';
import { ApiEndPoints } from '../../constants/apiEndPoints';

/** A file attached to a task (as posted, and as read back). */
export interface TaskAttachment {
  id?: number;
  file_url?: string;
  file_type?: string;
  name?: string;
}

/**
 * A task or subtask row. A subtask is just a task with `parent_task` set.
 * Backend field names vary, so values are read via the accessors below.
 */
export interface Task {
  id: number;
  task_name?: string | null;
  name?: string | null;
  description?: string | null;
  location?: string | null;
  engagement_id?: number | null;
  engagement?: number | null;
  parent_task?: number | null;
  category?: string | null;
  task_status?: string | null; // "Pending" | "In Progress" | "Completed" | "Assigned" | ...
  status?: string | null;
  priority?: string | null; // "Critical" | "High" | "Medium" | "Low"
  assigned_user?: number | { id?: number; full_name?: string; name?: string; avatar?: string } | null;
  assigned_user_name?: string | null;
  coordinator_name?: string | null;
  scheduled_date?: string | null;
  attachments?: TaskAttachment[] | null;
}

/** A coordinator option for the task assignment dropdown. */
export interface Coordinator {
  id: number;
  full_name?: string | null;
  name?: string | null;
  email?: string | null;
}

/** Request body for POST /task/ (task when `engagement_id` set, subtask when `parent_task` set). */
export interface CreateTaskPayload {
  task_name: string;
  description?: string;
  engagement_id?: number;
  parent_task?: number;
  category?: string;
  task_status?: string;
  priority?: string;
  assigned_user?: number | null;
  scheduled_date?: string;
  attachments?: TaskAttachment[];
}

/** DRF list responses may be a plain array or a paginated `{ results }` envelope. */
type Listish<T> = T[] | { count?: number; results?: T[] };
const asList = <T>(r: Listish<T>): T[] => (Array.isArray(r) ? r : (r?.results ?? []));

/** Normalized list result: current page rows plus the server's total count. */
export interface Paginated<T> {
  items: T[];
  total: number;
}
const toPaginated = <T>(r: Listish<T>): Paginated<T> => {
  const items = asList(r);
  const total = Array.isArray(r) ? r.length : (r?.count ?? items.length);
  return { items, total };
};

/* ------------------------------------------------------------------ *
 * Defensive accessors — exact field names aren't guaranteed.
 * ------------------------------------------------------------------ */
export const taskTitle = (t: Task) => t.task_name || t.name || 'Untitled task';
export const taskDesc = (t: Task) => t.description || '';
export const taskLocation = (t: Task) => t.location || t.description || '';
export const taskCategory = (t: Task) => t.category || '';
export const taskPriority = (t: Task) => t.priority || '';
export const taskDate = (t: Task) => t.scheduled_date || '';
export const taskRawStatus = (t: Task) => t.task_status || t.status || '';
export const taskAssigneeId = (t: Task): number | null => {
  if (t.assigned_user && typeof t.assigned_user === 'object') return t.assigned_user.id ?? null;
  return typeof t.assigned_user === 'number' ? t.assigned_user : null;
};
export const taskAssigneeName = (t: Task): string => {
  if (t.assigned_user_name) return t.assigned_user_name;
  if (t.coordinator_name) return t.coordinator_name;
  if (t.assigned_user && typeof t.assigned_user === 'object')
    return t.assigned_user.full_name || t.assigned_user.name || '';
  return '';
};
export const coordinatorName = (c: Coordinator) =>
  c.full_name || c.name || c.email || `User ${c.id}`;

/** Normalize a free-form API status into the UI status keys used by the designs. */
export type UiTaskStatus = 'completed' | 'inprogress' | 'pending' | 'assigned' | 'scheduled' | 'planning';
export const statusKey = (raw: string): UiTaskStatus => {
  const s = raw.toLowerCase().replace(/[\s_-]+/g, '');
  if (s.includes('complet')) return 'completed';
  if (s.includes('progress')) return 'inprogress';
  if (s.includes('assign')) return 'assigned';
  if (s.includes('schedul')) return 'scheduled';
  if (s.includes('plan')) return 'planning';
  return 'pending';
};

/** The API's canonical (title-case) status label for a UI key — used when writing back. */
export const API_STATUS: Record<UiTaskStatus, string> = {
  completed: 'Completed',
  inprogress: 'In Progress',
  pending: 'Pending',
  assigned: 'Assigned',
  scheduled: 'Scheduled',
  planning: 'Planning',
};

export const tasksApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    // Tasks belonging to an engagement (server-side search/status/category + pagination).
    getEngagementTasks: build.query<
      Paginated<Task>,
      {
        engagementId: number | string;
        search?: string;
        status?: string;
        category?: string;
        page?: number;
        pageSize?: number;
      }
    >({
      query: ({ engagementId, search, status, category, page, pageSize }) => ({
        url: `${ApiEndPoints.engagement}/${engagementId}/tasks/`,
        params: {
          ...(search ? { search } : {}),
          ...(status ? { status } : {}),
          ...(category ? { category } : {}),
          page: page ?? 1,
          page_size: pageSize ?? 10,
        },
      }),
      transformResponse: (r: Listish<Task>) => toPaginated(r),
      providesTags: ['Tasks'],
    }),
    // Subtasks of a task (tasks whose parent_task is the given id).
    getSubtasks: build.query<
      Paginated<Task>,
      {
        parentTask: number | string;
        search?: string;
        status?: string;
        priority?: string;
        page?: number;
        pageSize?: number;
      }
    >({
      query: ({ parentTask, search, status, priority, page, pageSize }) => ({
        url: `${ApiEndPoints.task}/`,
        params: {
          parent_task: parentTask,
          ...(search ? { search } : {}),
          ...(status ? { status } : {}),
          ...(priority ? { priority } : {}),
          page: page ?? 1,
          page_size: pageSize ?? 10,
        },
      }),
      transformResponse: (r: Listish<Task>) => toPaginated(r),
      providesTags: ['Tasks'],
    }),
    // A single task or subtask.
    getTask: build.query<Task, number | string>({
      query: (id) => ({ url: `${ApiEndPoints.task}/${id}/` }),
      providesTags: ['Tasks'],
    }),
    // Coordinator dropdown — users inside the engagement's boundary.
    getCoordinators: build.query<Coordinator[], number | string>({
      query: (engagementId) => ({
        url: `${ApiEndPoints.engagement}/${engagementId}/coordinators/`,
      }),
      transformResponse: (r: Listish<Coordinator>) => asList(r),
    }),
    createTask: build.mutation<Task, CreateTaskPayload>({
      query: (body) => ({ url: `${ApiEndPoints.task}/`, method: 'POST', body }),
      invalidatesTags: ['Tasks'],
    }),
    updateTask: build.mutation<Task, { id: number | string; patch: Partial<CreateTaskPayload> }>({
      query: ({ id, patch }) => ({
        url: `${ApiEndPoints.task}/${id}/`,
        method: 'PATCH',
        body: patch,
      }),
      invalidatesTags: ['Tasks'],
    }),
    deleteTask: build.mutation<void, number | string>({
      query: (id) => ({ url: `${ApiEndPoints.task}/${id}/`, method: 'DELETE' }),
      invalidatesTags: ['Tasks'],
    }),
  }),
});

export const {
  useGetEngagementTasksQuery,
  useGetSubtasksQuery,
  useGetTaskQuery,
  useGetCoordinatorsQuery,
  useCreateTaskMutation,
  useUpdateTaskMutation,
  useDeleteTaskMutation,
} = tasksApi;
